/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as Sentry from "@sentry/browser"; // eslint-disable-line import/no-namespace
import analytics from "web/script/analytics/analytics";

declare global {
    interface Window {
        NREUM: typeof import("new-relic-browser") | undefined;
    }
}

let lastException: any;
/**
 * Report an error to NewRelic (if available)
 */
function reportError(error: any, customAttributes?: Record<string, any>): void {
    if (window.NREUM) {
        (window.NREUM.noticeError as any)(error, customAttributes);
    }

    Sentry.captureException(error, {
        extra: {
            data: customAttributes,
        },
        contexts: {
            analytics: {
                page_view_id: analytics.getPageViewId(),
                device_id: analytics.getDeviceUid(),
            },
        },
    });
}

/**
 * Handle an error by sending data to New Relic (if available).
 */
function handleError(err: Error | Response | string, ...args: string[]): void {
    // checking for duplicate exceptions so we don't spam New Relic
    if (err === lastException) {
        return;
    }

    lastException = err;

    if (typeof Response != "undefined" && err instanceof Response) {
        try {
            throw new Error(
                `Request for ${err.url} responded with ${err.status} ${err.statusText}`
            );
        } catch (e) {
            err = e;
        }
    } else if (!(err instanceof Error)) {
        let message: string;
        try {
            message = JSON.stringify(err);
        } catch (e) {
            message = err + "";
        }
        try {
            throw new Error(message);
        } catch (e) {
            err = e;
        }
    }

    console.error(...args, err);

    reportError(err);
}

if (window.NREUM) {
    const SCRIPT_ERROR = /^Script error\.?$/i;
    const SHOPTAGR_QUERY = /\.oos-product-hero__section \.oos-product-hero__gt__cta p span/;
    const SAVED_SEARCH = /^Request for .+saved-search.+ responded with 404/;
    const FETCH_ERROR = /^Failed to make request to \/.+!$/;

    // Pick which errors we want to discard
    window.NREUM.setErrorHandler((err) => {
        // Try to deal with things that aren't Errors
        let message: string;
        if (typeof err == "string") {
            message = err;
        } else if (err && typeof err["message"] != "undefined") {
            message = err.message + "";
        } else {
            // I have no idea what this is so just let it through
            return false;
        }

        // Just in case there's stray whitespace somehow
        message = message.trim();

        if (SCRIPT_ERROR.test(message)) {
            // Useless unsolvable error caused by one of our third party scripts
            // See also: https://trackjs.com/blog/script-error-javascript-forensics/
            return true;
        } else if (
            err.message == "Cannot read property 'textContent' of undefined" ||
            SHOPTAGR_QUERY.test(message)
        ) {
            // Shoptagr is a very poorly coded extension that spews errors
            //  all over the place clogging up NewRelic.
            // Possibly we could work out some way to intentionally break
            //  it, but for now just block its errors.
            return true;
        } else if (SAVED_SEARCH.test(message)) {
            // We log an error for every failed request, even when the
            // "failure" is an expected 404 response
            return true;
        } else if (FETCH_ERROR.test(message)) {
            // While nominally we'd like to know about all these requests
            //  disappearing into the ether, they just completely swamp newrelic
            //  and none of them are actionable.
            // Ideally this should be increasing a metric somewhere but we don't
            //  have the ability to do that right now.
            return true;
        }
        return false;
    });
}

// public API
export default {
    error: handleError,
    reportError,
};
